import React, { useState } from 'react';
import { View, Text, } from '@constants/Themed';
import HorizontalCarousel from '@components/Global/Carousel/HorizontalCarousel';
import moment from 'moment';
import getDaysAgo from '@utils/getDaysAgo';
import ActionArray, { ArrayAction } from '@components/UI/ContextActions.tsx/ActionArray';
import { AntDesign, Entypo, FontAwesome6, Fontisto, FontAwesome5, MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import toUpperCase from '@hooks/toUpperCase';
import CreatorDisplay from '@components/Global/UserDisplay/CreatorDisplay';
import { Platform, Pressable, Alert, useWindowDimensions } from 'react-native';
import { XStack } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import ErrorBoundary from '@utils/ErrorBoundary';

const ICON_SIZE = 20;

type KnowledgeItemProps = {
  maxWidth?: number;
  item: {
    url?: string;
    _id: string;
    title: string;
    description: string;
    images: string[];
    featuredImage?: string;
    status: string;
    type: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    scrapedFrom?: string;
    scrapingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
    scrapingMetadata?: {
      relevance_score?: number;
      relevance_notes?: string;
      cleaned_by_ai?: boolean;
      key_points?: string[];
      [key: string]: any;
    };
    category?: string;
  },
  refetch: () => void;
  index: number,
  flipState: (item: any) => void;
  onAddToGroup?: (itemId: string) => void;
  draggable?: boolean;
  onDragStart?: (e: any, item: any) => void;
  onDragEnd?: (e: any) => void;
}

const KnowledgeItem = ({ refetch, maxWidth = 350, item, index, flipState, onAddToGroup, draggable = false, onDragStart, onDragEnd }: KnowledgeItemProps) => {
  const [activeComponent, setActiveComponent] = useState('knowledge');
  const navigation = useNavigation();
  const { isTablet } = useDeviceType();
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width: screenWidth } = useWindowDimensions();

  // Combine featured image with existing images, similar to ProductItem
  const getDisplayImages = () => {
    const images = [];
    
    // Add featured image first if it exists
    if (item.featuredImage) {
      images.push(item.featuredImage);
    }
    
    // Add other images
    if (item.images && item.images.length > 0) {
      images.push(...item.images);
    }
    
    return images;
  };

  const displayImages = getDisplayImages();

  // Theme-aware colors
  const colors = {
    background: theme ? '#ffffff' : '#374151',
    cardBackground: theme ? '#f8fafc' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    statusBadge: getStatusColor(item.status),
    iconColor: theme ? '#374151' : '#e5e7eb',
    shadow: theme ? '#000000' : '#000000',
    border: theme ? '#e5e7eb' : '#4b5563',
  };

  // Status color helper
  function getStatusColor(status: string) {
    switch (status?.toLowerCase()) {
      case 'published':
        return '#10b981'; // green
      case 'draft':
        return '#f59e0b'; // amber
      case 'archived':
        return '#6b7280'; // grey
      case 'research':
        return '#8b5cf6'; // purple
      default:
        return '#3b82f6'; // blue
    }
  }

  // Responsive sizing
  const imageSize = isTablet ? 140 : 120;
  const cardPadding = isTablet ? 20 : 16;

  const handleFlip = () => {
    flipState?.(item);
  }

  const handlePublish = async () => {
    if (item.status === 'draft') {
      await apis.publishKnowledgeArticle(item._id).then(() => {
        try {
          refetch();
        } catch (error) {
          console.error('Failed to refetch after publish:', error);
        }
      })
    } else {
      await apis.publishKnowledgeArticle(item._id, true).then(() => {
        try {
          refetch();
        } catch (error) {
          console.error('Failed to refetch after publish:', error);
        }
      })
    }
  }

  const handleNavigation = (screen: string, params?: any) => {
    try {
      (navigation as any).navigate(screen, params);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  // Calculate responsive action bar sizing based on available width
  // Account for card padding (left + right) and margins
  const availableWidth = Math.min(maxWidth, screenWidth - 32); // 32 for margins
  const actionBarPadding = cardPadding * 2; // left + right padding
  const actionBarWidth = availableWidth - actionBarPadding;

  // Define actions with icon metadata (will be rendered with responsive size)
  const baseActionCount = item.url ? 6 : 5; // Count actions before creating array

  // Scale button size based on available space
  // Minimum 36px for touch targets, but scale down if needed
  const baseButtonSize = 44;
  const minButtonSize = 36;
  const calculatedButtonSize = Math.max(
    minButtonSize,
    Math.min(baseButtonSize, (actionBarWidth / baseActionCount) - 8) // -8 for spacing
  );

  // Scale icon size proportionally
  const iconScale = calculatedButtonSize / baseButtonSize;
  const responsiveIconSize = Math.max(16, Math.floor(ICON_SIZE * iconScale));
  const buttonPadding = Math.max(4, Math.floor(8 * iconScale));

  // Helper function to render icon with responsive size
  const renderIcon = (IconComponent: any, name: string, color: string) => {
    return <IconComponent name={name} size={responsiveIconSize} color={color} />;
  };

  const actionArray: ArrayAction[] = [
    ...(item.url ? [{
      icon: renderIcon(FontAwesome6, "link", colors.iconColor),
      function: () => {
        Platform.OS === 'web'
          ? window.open(item.url, '_blank')
          : handleNavigation('WebView', { url: item.url });
      },
      functionName: 'link',
      text: ''
    }] : []),
    {
      icon: renderIcon(AntDesign, "edit", colors.iconColor),
      function: () => handleNavigation('Knowledge', { screen: 'EditKnowledge', params: { knowledgeArticleId: item._id } }),
      functionName: 'edit',
      text: ''
    },
    ...(onAddToGroup ? [{
      icon: renderIcon(MaterialIcons, "folder", colors.iconColor),
      function: () => {
        if (onAddToGroup) {
          onAddToGroup(item._id);
        }
      },
      functionName: 'addToGroup',
      text: 'Add to Group'
    }] : []),
    {
      icon: renderIcon(Entypo, "bookmark", colors.iconColor),
      function: () => console.log('bookmark'),
      functionName: 'bookmark',
      text: ''
    },
    {
      icon: renderIcon(Fontisto, "preview", colors.iconColor),
      function: handleFlip,
      functionName: 'preview',
      text: ''
    },
    {
      icon: renderIcon(Entypo, "publish", colors.iconColor),
      function: handlePublish,
      functionName: 'publish',
      text: ''
    },
    {
      icon: renderIcon(Entypo, "trash", "#ef4444"),
      function: () => {
        apis.deleteKnowledgeArticle(item._id)
        .then(() => {
          refetch();
        })
        .catch((error) => {
          console.error('Failed to delete knowledge article:', error);
        });
      },
      functionName: 'delete',
      text: ''
    },
  ];

  // Web drag handlers
  const handleWebDragStart = (e: any) => {
    if (Platform.OS === 'web' && draggable && onDragStart) {
      onDragStart(e, item);
    }
  };

  const handleWebDragEnd = (e: any) => {
    if (Platform.OS === 'web' && draggable && onDragEnd) {
      onDragEnd(e);
    }
  };

  return (
    <View
      draggable={Platform.OS === 'web' && draggable}
      onDragStart={handleWebDragStart}
      onDragEnd={handleWebDragEnd}
      style={{
        width: "100%",
        maxWidth: maxWidth,
        margin: 16,
        borderRadius: 16,
        backgroundColor: colors.background,
        shadowColor: colors.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 4,
        overflow: 'hidden',
        cursor: Platform.OS === 'web' && draggable ? 'grab' : 'default',
        ...(Platform.OS === 'web' && draggable && {
          userSelect: 'none' as any,
        }),
      }}
    >
      {/* Image Section - Now at the top */}
      <View style={{
        width: '100%',
        height: imageSize + 20, // Add some extra height for better proportions
        position: 'relative',
      }}>
        {displayImages.length > 0 ? (
          <>
            <ErrorBoundary>
              <HorizontalCarousel
                images={displayImages}
                height={imageSize + 20}
                imageStyle={{ 
                  borderRadius: 0, // Remove border radius for top image
                  width: '100%',
                  height: imageSize + 20,
                  objectFit: 'cover' as any
                }}
              />
            </ErrorBoundary>
            {/* Featured Image Indicator */}
            
            {/* Status Badge - moved to top right of image */}
            <View style={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: colors.statusBadge,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 8,
              zIndex: 10,
            }}>
              <Text style={{
                color: 'white',
                fontSize: 12,
                fontWeight: '600',
              }}>
                {toUpperCase(item.status) || "DRAFT"}
              </Text>
            </View>

            {/* Scraping Status Indicator for Research Articles */}
            {item.scrapingStatus && item.scrapingStatus !== 'pending' && item.status === 'research' && (
              <Pressable
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  backgroundColor:
                    item.scrapingStatus === 'completed' ? '#10b981' :
                      item.scrapingStatus === 'processing' ? '#f59e0b' :
                        '#ef4444',
                  borderRadius: 12,
                  padding: 6,
                  zIndex: 10,
                }}
                onPress={() => {
                  const statusMessages = {
                    processing: 'Article scraping in progress. Key points and content are being extracted.',
                    completed: 'Article successfully scraped and processed.',
                    failed: 'Article scraping failed. You may need to manually add content.'
                  };
                  Alert.alert(
                    'Scraping Status',
                    statusMessages[item.scrapingStatus] || 'Unknown status',
                    [{ text: 'OK' }]
                  );
                }}
              >
                {item.scrapingStatus === 'completed' && <FontAwesome5 name="check-circle" size={16} color="white" />}
                {item.scrapingStatus === 'processing' && <FontAwesome5 name="spinner" size={16} color="white" />}
                {item.scrapingStatus === 'failed' && <FontAwesome5 name="exclamation-circle" size={16} color="white" />}
              </Pressable>
            )}
          </>
        ) : (
          // Placeholder when no images available
          <View style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: colors.border,
            position: 'relative',
          }}>
            <AntDesign 
              name="picture" 
              size={40} 
              color={colors.textSecondary} 
              style={{ opacity: 0.5 }}
            />
            <Text style={{
              color: colors.textSecondary,
              fontSize: 12,
              marginTop: 8,
              textAlign: 'center',
              opacity: 0.7,
            }}>
              No Image
            </Text>
            {/* Status Badge for no-image state */}
            <View style={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: colors.statusBadge,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 8,
              zIndex: 10,
            }}>
              <Text style={{
                color: 'white',
                fontSize: 12,
                fontWeight: '600',
              }}>
                {toUpperCase(item.status) || "DRAFT"}
              </Text>
            </View>

              {/* Scraping Status Indicator for Research Articles (no-image state) */}
              {item.scrapingStatus && item.scrapingStatus !== 'pending' && item.status === 'research' && (
                <Pressable
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor:
                      item.scrapingStatus === 'completed' ? '#10b981' :
                        item.scrapingStatus === 'processing' ? '#f59e0b' :
                          '#ef4444',
                    borderRadius: 12,
                    padding: 6,
                    zIndex: 10,
                  }}
                  onPress={() => {
                    const statusMessages = {
                      processing: 'Article scraping in progress. Key points and content are being extracted.',
                      completed: 'Article successfully scraped and processed.',
                      failed: 'Article scraping failed. You may need to manually add content.'
                    };
                    Alert.alert(
                      'Scraping Status',
                      statusMessages[item.scrapingStatus] || 'Unknown status',
                      [{ text: 'OK' }]
                    );
                  }}
                >
                  {item.scrapingStatus === 'completed' && <FontAwesome5 name="check-circle" size={16} color="white" />}
                  {item.scrapingStatus === 'processing' && <FontAwesome5 name="spinner" size={16} color="white" />}
                  {item.scrapingStatus === 'failed' && <FontAwesome5 name="exclamation-circle" size={16} color="white" />}
                </Pressable>
              )}
          </View>
        )}
      </View>

      {/* Content Section - Now below the image */}
      <View style={{
        padding: cardPadding,
        paddingBottom: 80, // Space for action bar
        flex: 1,
      }}>
        {/* Title and Description */}
        <View style={{ marginBottom: 16 }}>
          <Text style={{
            color: colors.text,
            fontWeight: 'bold',
            fontSize: 18,
            marginBottom: 8,
            lineHeight: 24,
          }} numberOfLines={2}>
            {item.title}
          </Text>

          <Text style={{
            color: colors.textSecondary,
            fontSize: 14,
            lineHeight: 20,
            fontStyle: item.description ? 'normal' : 'italic',
          }} numberOfLines={3}>
            {item.description || 'No description available'}
          </Text>

          {/* Relevance Score for Research Articles */}
          {item.status === 'research' && item.scrapingMetadata?.relevance_score !== undefined && (
            <Pressable
              onPress={() => {
                Alert.alert(
                  'Content Relevance',
                  item.scrapingMetadata?.relevance_notes || 'This article has been analyzed for relevance to your business context.',
                  [{ text: 'OK' }]
                );
              }}
              style={{
                marginTop: 8,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingVertical: 4,
                paddingHorizontal: 8,
                backgroundColor:
                  item.scrapingMetadata.relevance_score >= 70 ? 'rgba(16, 185, 129, 0.1)' :
                    item.scrapingMetadata.relevance_score >= 40 ? 'rgba(245, 158, 11, 0.1)' :
                      'rgba(239, 68, 68, 0.1)',
                borderRadius: 8,
                borderWidth: 1,
                borderColor:
                  item.scrapingMetadata.relevance_score >= 70 ? '#10b981' :
                    item.scrapingMetadata.relevance_score >= 40 ? '#f59e0b' :
                      '#ef4444',
                alignSelf: 'flex-start'
              }}
            >
              <FontAwesome5
                name="bullseye"
                size={12}
                color={
                  item.scrapingMetadata.relevance_score >= 70 ? '#10b981' :
                    item.scrapingMetadata.relevance_score >= 40 ? '#f59e0b' :
                      '#ef4444'
                }
              />
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color:
                  item.scrapingMetadata.relevance_score >= 70 ? '#10b981' :
                    item.scrapingMetadata.relevance_score >= 40 ? '#f59e0b' :
                      '#ef4444'
              }}>
                {item.scrapingMetadata.relevance_score}% Relevant
              </Text>
            </Pressable>
          )}
        </View>

        {/* Creator and Date Info */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}>
          <View style={{ flex: 1 }}>
            <Text style={{
              color: colors.textSecondary,
              fontSize: 12,
              marginBottom: 4,
            }}>
              {moment(item.createdAt).format("DD/MM/YYYY HH:mm")}
            </Text>
            <Text style={{
              color: colors.textSecondary,
              fontSize: 12,
            }}>
              Updated: {getDaysAgo(item.updatedAt)}
            </Text>
          </View>

          {/* Creator Display - Simplified for now due to type issues */}
          <View style={{
            backgroundColor: colors.cardBackground,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 6,
          }}>
            <Text style={{
              color: colors.textSecondary,
              fontSize: 11,
              fontWeight: '500',
            }}>
              Creator
            </Text>
          </View>
        </View>
      </View>

      {/* Action Bar */}
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-around',
        position: "absolute",
        bottom: 0,
        alignSelf: 'stretch',
        alignItems: 'center',
        width: '100%',
        paddingVertical: Math.max(8, 12 * iconScale),
        paddingHorizontal: cardPadding,
        backgroundColor: colors.cardBackground,
        borderTopWidth: 1,
        borderTopColor: colors.border,
      }}>
        {actionArray.map((action, index) => (
          <Pressable
            key={action.functionName}
            style={({ pressed }) => ({
              flex: 1,
              padding: buttonPadding,
              borderRadius: 8,
              minHeight: calculatedButtonSize,
              maxHeight: calculatedButtonSize,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: pressed ? (theme ? '#f3f4f6' : '#374151') : 'transparent',
              opacity: pressed ? 0.8 : 1,
            })}
            onPress={() => action.function(action.functionName)}
            android_ripple={{ color: colors.textSecondary, borderless: true }}
            accessibilityRole="button"
            accessibilityLabel={`${action.functionName} action`}
            accessibilityHint={`Tap to ${action.functionName} this knowledge item`}
          >
            {action.icon}
          </Pressable>
        ))}
      </View>
    </View>
  )
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(KnowledgeItem);
